import React, { useState } from 'react';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  Input,
  Modal,
  Form,
  Select,
  DatePicker,
  Typography,
  Row,
  Col,
  Tooltip,
  Badge,
  Statistic,
  message,
  Drawer,
  List,
  ConfigProvider
} from 'antd';
import {
  FilterOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import dayjs from 'dayjs';
import 'dayjs/locale/vi';
import locale from 'antd/locale/vi_VN';
import api from '../../services/api';
import { stockService } from '../../services/stockService';
import styles from './StockOutPage.module.css';
import { StockOutModal, StockOutPrintModal } from '../../components/stock-out';

const { Text } = Typography;
const { Search } = Input;
const { Option } = Select;


interface StockOutItem {
  id: number;
  medicine_id: number;
  medicine_name: string;
  medicine_code: string;
  quantity: number;
  unit: string;
  unit_price: number;
  lot_id: number;
  lot_number: string;
  expiry_date: string;
  total_amount: number;
  notes: string;
  available_stock: number;
}

interface StockOutRecord {
  id: number;
  reference_number?: string;
  voucher_number?: string;
  customer_name?: string;
  customer_phone?: string;
  customer_address?: string;
  total_items?: number;
  total_amount?: number;
  status?: 'pending' | 'completed' | 'cancelled';
  created_at?: string;
  completed_at?: string;
  created_by?: string;
  created_by_name?: string;
  notes?: string;
  items?: StockOutItem[];
  // For API detail response structure
  voucher?: {
    id: number;
    voucher_number: string;
    customer_name: string;
    customer_phone: string;
    customer_address: string;
    total_amount: number;
    notes: string;
    created_by: string;
    created_at: string;
    status: string;
  };
}



const StockOutPage: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    customer: '',
    date_from: dayjs().startOf('month').format('YYYY-MM-DD'),
    date_to: dayjs().endOf('month').format('YYYY-MM-DD')
  } as {
    status: string;
    customer: string;
    date_from: string | undefined;
    date_to: string | undefined;
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [printModalVisible, setPrintModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<StockOutRecord | null>(null);
  const [editingRecord, setEditingRecord] = useState<StockOutRecord | null>(null);
  const [printingRecordId, setPrintingRecordId] = useState<number | null>(null);
  const [form] = Form.useForm();

  // State cho phân trang
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });

  const queryClient = useQueryClient();

  // Fetch data
  const { data: stockOutRecords = [], isLoading } = useQuery(
    ['stock-out', searchText, filters],
    () => stockService.getStockOutVouchers({
        search: searchText,
        status: filters.status,
        customer: filters.customer,
        start_date: filters.date_from,
        end_date: filters.date_to
    }),
    {
      staleTime: 5 * 60 * 1000,
    }
  );

  const { data: medicines = [] } = useQuery(
    'medicines',
    () => api.get('/api/medicines').then(res => res.data.data),
    {
      staleTime: 10 * 60 * 1000,
    }
  );

  // Fetch detailed record for printing
  const { data: printDetailRecord, isLoading: isLoadingPrintDetail } = useQuery(
    ['stock-out-print-detail', printingRecordId],
    () => stockService.getStockOutVoucherDetail(printingRecordId!),
    {
      enabled: !!printingRecordId && printModalVisible,
      staleTime: 0,
      cacheTime: 0,
    }
  );

  // Fetch detailed record for editing
  const { data: editDetailRecord, isLoading: isLoadingEditDetail } = useQuery(
    ['stock-out-edit-detail', editingRecord?.id],
    () => stockService.getStockOutVoucherDetail(editingRecord!.id),
    {
      enabled: !!editingRecord?.id && modalVisible,
      staleTime: 1 * 60 * 1000,
    }
  );

  // Fetch detailed record for viewing
  const { data: viewDetailRecord, isLoading: isLoadingViewDetail } = useQuery(
    ['stock-out-view-detail', selectedRecord?.id],
    () => stockService.getStockOutVoucherDetail(selectedRecord!.id),
    {
      enabled: !!selectedRecord?.id && drawerVisible,
      staleTime: 1 * 60 * 1000,
      onSuccess: (data) => {
        console.log('View detail record loaded:', data);
        console.log('Voucher data:', data.voucher);
        console.log('Items data:', data.items);
        console.log('Items count:', data.items?.length || 0);
      },
      onError: (error) => {
        console.error('Error loading view detail:', error);
      }
    }
  );

  // Mutations
  const createMutation = useMutation(
    (data: any) => stockService.createStockOutVoucher(data),
    {
      onSuccess: () => {
        message.success('Tạo phiếu xuất kho thành công!');
        queryClient.invalidateQueries('stock-out');
        setModalVisible(false);
        form.resetFields();
      },
      onError: (error: any) => {
        console.error('Create stock out error:', error);
        message.error(error.response?.data?.message || 'Có lỗi xảy ra khi tạo phiếu xuất kho');
      },
    }
  );

  const updateMutation = useMutation(
    ({ id, data }: { id: number; data: any }) => stockService.updateStockOutVoucher(id, data),
    {
      onSuccess: () => {
        message.success('Cập nhật phiếu xuất kho thành công!');
        queryClient.invalidateQueries('stock-out');
        queryClient.invalidateQueries('stock-out-edit-detail');
        setModalVisible(false);
        setEditingRecord(null);
        form.resetFields();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Có lỗi xảy ra khi cập nhật phiếu xuất kho');
      },
    }
  );

  const completeMutation = useMutation(
    (id: number) => stockService.completeStockOutVoucher(id),
    {
      onSuccess: () => {
        message.success('Hoàn thành xuất kho thành công!');
        queryClient.invalidateQueries('stock-out');
        queryClient.invalidateQueries('inventory');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Có lỗi xảy ra khi hoàn thành xuất kho');
      },
    }
  );

  const deleteMutation = useMutation(
    (id: number) => stockService.deleteStockOutVoucher(id),
    {
      onSuccess: () => {
        message.success('Xóa phiếu xuất kho thành công!');
        queryClient.invalidateQueries('stock-out');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Có lỗi xảy ra khi xóa phiếu xuất kho');
      },
    }
  );

  // Handlers
  const handleAdd = () => {
    setEditingRecord(null);
    setModalVisible(true);
    form.resetFields();
  };

  const handleEdit = (record: StockOutRecord) => {
    setEditingRecord(record);
    setModalVisible(true);
  };

  const handleView = (record: StockOutRecord) => {
    setSelectedRecord(record);
    setDrawerVisible(true);
  };

  const handlePrint = async (record: StockOutRecord) => {
    setPrintingRecordId(record.id);
    await queryClient.removeQueries(['stock-out-print-detail', record.id]);
    setPrintModalVisible(true);
  };

  const handleComplete = (id: number) => {
    Modal.confirm({
      title: 'Xác nhận hoàn thành xuất kho',
      content: 'Bạn có chắc chắn muốn hoàn thành phiếu xuất kho này? Hành động này sẽ cập nhật tồn kho.',
      okText: 'Hoàn thành',
      cancelText: 'Hủy',
      onOk: () => completeMutation.mutate(id),
    });
  };

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: 'Xác nhận xóa phiếu xuất kho',
      content: 'Bạn có chắc chắn muốn xóa phiếu xuất kho này? Hành động này không thể hoàn tác.',
      okText: 'Xóa',
      cancelText: 'Hủy',
      okType: 'danger',
      onOk: () => deleteMutation.mutate(id),
    });
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingRecord(null);
    form.resetFields();
    // Clear the edit detail query cache
    queryClient.removeQueries(['stock-out-edit-detail']);
  };

  const handlePrintModalCancel = () => {
    setPrintModalVisible(false);
    setPrintingRecordId(null);
  };

  const handleSubmit = async (values: any) => {
    try {
      console.log('HandleSubmit values:', values);
      console.log('Customer ID in handleSubmit:', values.customer_id);
      
      const data: any = {
        customer_id: values.customer_id || null,
        customer_name: values.customer_name,
        customer_phone: values.customer_phone,
        customer_address: values.customer_address,
        notes: values.notes || '',
        items: values.items.map((item: any) => {
          const itemData: any = {
            medicine_id: item.medicine_id,
            quantity: item.quantity,
            unit_price: item.unit_price,
            lot_id: item.lot_id,
            lot_number: item.lot_number || 'N/A',
            notes: item.notes || ''
          };
          
          // Only include ID if it exists (for existing items)
          if (item.id) {
            itemData.id = item.id;
          }
          
          // Include voucher_id if available
          if (item.voucher_id) {
            itemData.voucher_id = item.voucher_id;
          }
          
          // Ensure lot_id is a number or null
          if (itemData.lot_id === undefined || itemData.lot_id === '') {
            itemData.lot_id = null;
          }
          
          return itemData;
        })
      };

              console.log('HandleSubmit data:', data);
        console.log('Data items count:', data.items.length);
        console.log('Data items with ID:', data.items.filter((item: any) => item.id).length);
        console.log('Data items without ID:', data.items.filter((item: any) => !item.id).length);

      if (editingRecord) {
        // Use ID from detail record if available, otherwise from list record
        const recordId = editDetailRecord?.voucher?.id || editDetailRecord?.id || editingRecord.id;
        console.log('Using record ID:', recordId);
        console.log('EditDetailRecord:', editDetailRecord);
        console.log('EditingRecord:', editingRecord);
        
        // Add voucher_id to data if available
        if (editDetailRecord?.voucher?.id) {
          data.voucher_id = editDetailRecord.voucher.id;
        }
        
        console.log('Final data being sent to API:', data);
        await updateMutation.mutateAsync({ id: recordId, data });
      } else {
        console.log('Final data being sent to API (create):', data);
        await createMutation.mutateAsync(data);
      }
    } catch (error) {
      console.error('HandleSubmit error:', error);
      // Error handling is done in mutation
    }
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
  };

  const clearFilters = () => {
    setFilters({ 
      status: '', 
      customer: '', 
      date_from: dayjs().startOf('month').format('YYYY-MM-DD'),
      date_to: dayjs().endOf('month').format('YYYY-MM-DD')
    });
    setSearchText('');
  };

  // Statistics
  const totalRecords = stockOutRecords.length;
  const pendingRecords = stockOutRecords.filter((r: StockOutRecord) => r.status === 'pending').length;
  const completedRecords = stockOutRecords.filter((r: StockOutRecord) => r.status === 'completed').length;
  const totalAmount = stockOutRecords.reduce((sum: number, r: StockOutRecord) => sum + (r.total_amount || 0), 0);

  const getSummaryStats = () => {
    return (
      <div className={styles.statsContainer}>
        <Row gutter={[24, 16]} className={styles.statsRow}>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#3b82f6' }}>
                  <i className="fas fa-box-open" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Tổng phiếu xuất"
                    value={totalRecords}
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                  />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#f59e0b' }}>
                  <i className="fas fa-clock" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Chờ xử lý"
                    value={pendingRecords}
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                  />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#10b981' }}>
                  <i className="fas fa-check-circle" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Đã hoàn thành"
                    value={completedRecords}
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                  />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#8b5cf6' }}>
                  <i className="fas fa-dollar-sign" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Tổng giá trị"
                    value={totalAmount}
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                    formatter={(value) => value?.toLocaleString('vi-VN')}
                  />
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  // Filtered data
  const filteredRecords = stockOutRecords.filter((record: StockOutRecord) => {
    const matchesStatus = !filters.status || record.status === filters.status;
    
    // Tìm kiếm gần đúng theo mã phiếu hoặc tên khách hàng (không phân biệt hoa thường)
    const matchesSearch = !searchText || (() => {
      const searchLower = searchText.toLowerCase().trim();
      if (!searchLower) return true;
      
      // Loại bỏ dấu tiếng Việt để tìm kiếm gần đúng hơn
      const removeVietnameseAccents = (str: string) => {
        return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      };
      
      const searchNormalized = removeVietnameseAccents(searchLower);
      
      // Tìm kiếm trong mã phiếu
      const voucherNumber = (record.reference_number || record.voucher_number || '').toLowerCase();
      const voucherNormalized = removeVietnameseAccents(voucherNumber);
      
      // Tìm kiếm trong tên khách hàng
      const customerName = (record.customer_name || '').toLowerCase();
      const customerNormalized = removeVietnameseAccents(customerName);
      
      return voucherNormalized.includes(searchNormalized) || 
             customerNormalized.includes(searchNormalized);
    })();
    
    return matchesStatus && matchesSearch;
  });

  // Cập nhật lại tổng số bản ghi khi filter thay đổi
  React.useEffect(() => {
    setPagination((prev) => ({ ...prev, total: filteredRecords.length, current: 1 }));
  }, [filteredRecords.length, searchText, filters]);

  // Lấy dữ liệu trang hiện tại
  const pagedRecords = React.useMemo(() => {
    const start = (pagination.current - 1) * pagination.pageSize;
    const end = start + pagination.pageSize;
    return filteredRecords.slice(start, end);
  }, [filteredRecords, pagination]);

  const columns = [
    {
      title: 'Mã phiếu',
      dataIndex: 'reference_number',
      key: 'reference_number',
      width: 160,
      fixed: 'left' as const,
      render: (ref: string, record: StockOutRecord) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ 
            fontWeight: '600', 
            fontSize: '15px',
            color: '#1f2937',
            lineHeight: '1.2'
          }}>
            {ref || record.voucher_number || 'N/A'}
          </div>
          <div style={{ 
            fontSize: '12px', 
            color: '#6b7280',
            fontWeight: '400'
          }}>
            {record.created_at ? dayjs(record.created_at).format('DD/MM/YYYY HH:mm') : 'N/A'}
          </div>
        </div>
      ),
    },
    {
      title: 'Khách hàng',
      key: 'customer',
      width: 200,
      render: (_: any, record: StockOutRecord) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ 
            fontWeight: '500', 
            fontSize: '14px',
            color: '#1f2937',
            lineHeight: '1.3'
          }}>
            {record.customer_name || 'Khách lẻ'}
          </div>
          {record.customer_phone && (
            <div style={{ 
              fontSize: '12px', 
              color: '#6b7280',
              fontWeight: '400'
            }}>
              {record.customer_phone}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Thông tin',
      key: 'info',
      width: 140,
      render: (_: any, record: StockOutRecord) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '6px',
            fontSize: '13px',
            color: '#374151'
          }}>
            <i className="fas fa-box" style={{ color: '#6b7280', fontSize: '12px' }} />
            <span style={{ fontWeight: '500' }}>{record.total_items || 0} món</span>
          </div>
          <div style={{ 
            fontSize: '14px',
            fontWeight: '600',
            color: '#059669'
          }}>
            {(record.total_amount || 0).toLocaleString('vi-VN')}đ
          </div>
        </div>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const statusConfig = {
          pending: { 
            color: '#f59e0b', 
            bgColor: '#fef3c7',
            text: 'Chờ xử lý',
            icon: 'fas fa-clock'
          },
          completed: { 
            color: '#059669', 
            bgColor: '#d1fae5',
            text: 'Hoàn thành',
            icon: 'fas fa-check-circle'
          },
          cancelled: { 
            color: '#dc2626', 
            bgColor: '#fee2e2',
            text: 'Đã hủy',
            icon: 'fas fa-times-circle'
          }
        };
        const config = statusConfig[status as keyof typeof statusConfig];
        return (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '6px',
            padding: '6px 12px',
            borderRadius: '20px',
            backgroundColor: config.bgColor,
            color: config.color,
            fontSize: '12px',
            fontWeight: '500',
            width: 'fit-content'
          }}>
            <i className={config.icon} style={{ fontSize: '10px' }} />
            {config.text}
          </div>
        );
      },
      filters: [
        { text: 'Chờ xử lý', value: 'pending' },
        { text: 'Hoàn thành', value: 'completed' },
        { text: 'Đã hủy', value: 'cancelled' }
      ],
      onFilter: (value: any, record: StockOutRecord) => record.status === value,
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 120,
      fixed: 'right' as const,
      render: (_: any, record: StockOutRecord) => (
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <Tooltip title="Xem chi tiết">
            <Button
              type="text"
              icon={<i className="fas fa-eye" />}
              onClick={() => handleView(record)}
              size="small"
              style={{ 
                color: '#6b7280',
                border: 'none',
                boxShadow: 'none'
              }}
            />
          </Tooltip>
          <Tooltip title="In phiếu">
            <Button
              type="text"
              icon={<i className="fas fa-print" />}
              onClick={() => handlePrint(record)}
              size="small"
              style={{ 
                color: '#6b7280',
                border: 'none',
                boxShadow: 'none'
              }}
            />
          </Tooltip>
          {record.status === 'pending' && (
            <>
              <Tooltip title="Sửa">
                <Button
                  type="text"
                  icon={<i className="fas fa-edit" />}
                  onClick={() => handleEdit(record)}
                  size="small"
                  style={{ 
                    color: '#f59e0b',
                    border: 'none',
                    boxShadow: 'none'
                  }}
                />
              </Tooltip>
              <Tooltip title="Hoàn thành">
                <Button
                  type="text"
                  icon={<i className="fas fa-check-circle" />}
                  onClick={() => handleComplete(record.id)}
                  size="small"
                  style={{ 
                    color: '#059669',
                    border: 'none',
                    boxShadow: 'none'
                  }}
                />
              </Tooltip>
              <Tooltip title="Xóa">
                <Button
                  type="text"
                  icon={<i className="fas fa-trash" />}
                  onClick={() => handleDelete(record.id)}
                  size="small"
                  style={{ 
                    color: '#dc2626',
                    border: 'none',
                    boxShadow: 'none'
                  }}
                />
              </Tooltip>
            </>
          )}
        </div>
      ),
    },
  ];

  // Thiết lập locale cho dayjs
  dayjs.locale('vi');

  return (
    <ConfigProvider locale={locale}>
      <div className={styles.container}>
        {getSummaryStats()}

        <Card className={styles.tableCard}>
          <div className={styles.tableHeader}>
            <div className={styles.searchSection}>
              <Search
                placeholder="Tìm kiếm theo mã phiếu, tên khách hàng (không phân biệt hoa thường)..."
                allowClear
                className={styles.searchInput}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onSearch={handleSearch}
                size="large"
                style={{ textAlign: 'left' }}
              />
            </div>
            <div className={styles.filterSection}>
              <Space size="middle" wrap>
                <DatePicker
                  placeholder="Từ ngày"
                  value={filters.date_from ? dayjs(filters.date_from) : undefined}
                  onChange={(date) => setFilters(prev => ({ ...prev, date_from: date ? dayjs(date).format('YYYY-MM-DD') : undefined }))}
                  style={{ minWidth: 150 }}
                />
                <DatePicker
                  placeholder="Đến ngày"
                  value={filters.date_to ? dayjs(filters.date_to) : undefined}
                  onChange={(date) => setFilters(prev => ({ ...prev, date_to: date ? dayjs(date).format('YYYY-MM-DD') : undefined }))}
                  style={{ minWidth: 150 }}
                />
                <Select
                  placeholder="Lọc theo trạng thái"
                  allowClear
                  value={filters.status}
                  onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
                  style={{ minWidth: 150 }}
                >
                  <Option value="pending">Chờ xử lý</Option>
                  <Option value="completed">Hoàn thành</Option>
                  <Option value="cancelled">Đã hủy</Option>
                </Select>
                {(filters.status || filters.date_from || filters.date_to || searchText) && (
                  <Button
                    type="link"
                    icon={<FilterOutlined />}
                    onClick={clearFilters}
                    size="small"
                  >
                    Xóa bộ lọc
                  </Button>
                )}
              </Space>
            </div>
            <div className={styles.actionButtons}>
              <Space size="middle">
                <Tooltip title="Làm mới dữ liệu">
                  <Button
                    icon={<i className="fas fa-sync-alt" />}
                    onClick={() => window.location.reload()}
                    className={styles.actionButton}
                  >
                    Làm mới
                  </Button>
                </Tooltip>
                <Tooltip title="Tạo phiếu xuất kho mới">
                  <Button
                    type="primary"
                    icon={<i className="fas fa-plus" />}
                    onClick={handleAdd}
                    className={styles.primaryButton}
                  >
                    Tạo phiếu xuất
                  </Button>
                </Tooltip>
              </Space>
            </div>
          </div>

          <Table
            columns={columns}
            dataSource={pagedRecords}
            rowKey="id"
            loading={isLoading}
            className={styles.stockOutTable}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} của ${total} phiếu xuất kho`,
              className: styles.tablePagination,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page, pageSize) => setPagination(prev => ({ ...prev, current: page, pageSize }))
            }}
            size="middle"
            scroll={{ x: 800 }}
          />
        </Card>

      {/* Detail Drawer */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="fas fa-file-alt" />
            Chi tiết phiếu xuất kho
            {isLoadingViewDetail && (
              <Text type="secondary" style={{ fontSize: '14px', marginLeft: '8px' }}>
                (Đang tải...)
              </Text>
            )}
          </div>
        }
        placement="right"
        width={600}
        onClose={() => {
          setDrawerVisible(false);
          setSelectedRecord(null);
          // Clear the view detail query cache
          queryClient.removeQueries(['stock-out-view-detail']);
        }}
        open={drawerVisible}
        loading={isLoadingViewDetail}
      >
        {viewDetailRecord && (
          <div>
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic
                    title="Mã phiếu"
                    value={viewDetailRecord.voucher?.voucher_number || viewDetailRecord.voucher_number}
                    valueStyle={{ fontSize: '16px' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Tổng tiền"
                    value={viewDetailRecord.voucher?.total_amount || viewDetailRecord.total_amount}
                    valueStyle={{ color: '#52c41a' }}
                    formatter={(value) => value?.toLocaleString('vi-VN')}
                  />
                </Col>
              </Row>
            </Card>

            <Card size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <div style={{ marginBottom: 8 }}>
                    <Text strong>Khách hàng:</Text>
                  </div>
                  <div>{viewDetailRecord.voucher?.customer_name || viewDetailRecord.customer_name}</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {viewDetailRecord.voucher?.customer_phone || viewDetailRecord.customer_phone}
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ marginBottom: 8 }}>
                    <Text strong>Trạng thái:</Text>
                  </div>
                  <Tag color={(viewDetailRecord.voucher?.status || viewDetailRecord.status) === 'completed' ? 'green' : 'orange'}>
                    {(viewDetailRecord.voucher?.status || viewDetailRecord.status) === 'completed' ? 'Hoàn thành' : 'Chờ xử lý'}
                  </Tag>
                </Col>
              </Row>
              {(viewDetailRecord.voucher?.customer_address || viewDetailRecord.customer_address) && (
                <div style={{ marginTop: 8 }}>
                  <Text strong>Địa chỉ:</Text>
                  <div>{viewDetailRecord.voucher?.customer_address || viewDetailRecord.customer_address}</div>
                </div>
              )}
            </Card>

            <Card size="small" style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8 }}>
                <Text strong>Danh sách thuốc ({viewDetailRecord.items?.length || 0} thuốc):</Text>
              </div>
              {viewDetailRecord.items && viewDetailRecord.items.length > 0 ? (
                <List
                  dataSource={viewDetailRecord.items}
                  renderItem={(item: StockOutItem) => (
                    <List.Item>
                      <List.Item.Meta
                        title={
                          <div>
                            <div style={{ fontWeight: 'bold' }}>{item.medicine_name}</div>
                            <div style={{ fontSize: '12px', color: '#666' }}>Mã: {item.medicine_code}</div>
                          </div>
                        }
                        description={
                          <div>
                            <div><strong>Số lượng:</strong> {item.quantity} {item.unit}</div>
                            <div><strong>Đơn giá:</strong> {item.unit_price.toLocaleString('vi-VN')}</div>
                            <div><strong>Thành tiền:</strong> {(item.quantity * item.unit_price).toLocaleString('vi-VN')}</div>
                            {item.lot_number && <div><strong>Số lô:</strong> {item.lot_number}</div>}
                            {item.notes && <div><strong>Ghi chú:</strong> {item.notes}</div>}
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                  Không có thuốc nào trong phiếu này
                </div>
              )}
            </Card>

            {(viewDetailRecord.voucher?.notes || viewDetailRecord.notes) && (
              <Card size="small">
                <div style={{ marginBottom: 8 }}>
                  <Text strong>Ghi chú:</Text>
                </div>
                <div>{viewDetailRecord.voucher?.notes || viewDetailRecord.notes}</div>
              </Card>
            )}
          </div>
        )}
      </Drawer>

      {/* Stock Out Modal */}
      <StockOutModal
        visible={modalVisible}
        onCancel={handleModalCancel}
        onSubmit={handleSubmit}
        editingRecord={editDetailRecord || editingRecord}
        medicines={medicines}
        isLoadingDetail={isLoadingEditDetail}
      />

      {/* Print Modal */}
      <StockOutPrintModal
        visible={printModalVisible}
        onCancel={handlePrintModalCancel}
        data={printDetailRecord || null}
        isLoading={isLoadingPrintDetail}
      />
    </div>
    </ConfigProvider>
  );
};

export default StockOutPage; 